import { AppRoutePaths, EndSessionRoutePaths } from '@core/constants';
import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subject, defer } from 'rxjs';
import { exhaustMap } from 'rxjs/operators';

import { LoadingService } from '@core/services/loading.service';
import { LogService } from '@core/services/log.service';
import { NavigationService } from '@core/services/navigation.service';
import { PageKey } from '@core/enums/page-keys.enum';
import { Router } from '@angular/router';
import { ServiceInjector } from '@core/services/service-injector.module';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class BaseComponent implements OnDestroy {
  destroy$ = new Subject<void>();
  protectedProcess$: Subject<() => Promise<unknown>>;
  public _router: Router;
  private _navigationService: NavigationService;
  private _logService: LogService;
  public isRunningProcess = false;
  public _loadingService: LoadingService;

  constructor() {
    this._router = ServiceInjector.get<Router>(Router);
    this._navigationService = ServiceInjector.get<NavigationService>(NavigationService);
    this._logService = ServiceInjector.get<LogService>(LogService);
    this._loadingService = ServiceInjector.get<LoadingService>(LoadingService);
    this.initNavigationClick();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initNavigationClick() {
    this.protectedProcess$ = new Subject();
    this.protectedProcess$
      .asObservable()
      .pipe(
        exhaustMap((functionToCall) =>
          defer(async () => {
            try {
              this.isRunningProcess = true;
              await functionToCall();
            } catch (error) {
              this.logNavigationError('protectedClick()', error);
            } finally {
              this.isRunningProcess = false;
            }
          }),
        ),
      )
      .subscribe();
  }

  protected async navigateNext(skipLoader?: boolean) {
    try {
      if (!skipLoader) {
        this._loadingService.startLoading();
      }

      const page = await this._navigationService.next();
      await this.navigateToPage(page);
    } catch (error) {
      this.logNavigationError('navigateNext()', error);
      await this.navigateError(error);
    }
    this._loadingService.stopLoading();
  }

  protected async navigateBack() {
    this._loadingService.startLoading();
    try {
      const page = await this._navigationService.back();
      await this.navigateToPage(page);
    } catch (error) {
      this.logNavigationError('navigateBack()', error);
      await this.navigateError(error);
    }
    this._loadingService.stopLoading();
  }

  protected async navigateExit() {
    try {
      const page = await this._navigationService.exit();
      await this.navigateToPage(page);
    } catch (error) {
      this.logNavigationError('navigateExit()', error);
      await this.navigateError(error);
    }
  }

  protected async navigateBypass() {
    this._loadingService.startLoading();
    try {
      const page = await this._navigationService.bypass();
      await this.navigateToPage(page);
    } catch (error) {
      this.logNavigationError('navigateBypass()', error);
      await this.navigateError(error);
    }
    this._loadingService.stopLoading();
  }

  protected async navigateRefresh() {
    try {
      await this._router.navigateByUrl(this._router.url, { onSameUrlNavigation: 'reload' });
    } catch (error) {
      this.logNavigationError('navigateRefresh()', error);
      await this.navigateError(error);
    }
  }

  protected async navigateError(_error?: unknown) {
    return await this.navigateTo(`${AppRoutePaths.Error}`);
  }

  protected async navigateNotFound() {
    return await this.navigateTo(`${AppRoutePaths.NotFound}`);
  }

  protected async navigateEndSessionConfirmation() {
    this._loadingService.stopLoading();
    return await this.navigateTo(
      `${AppRoutePaths.EndSession}/${EndSessionRoutePaths.ClosingSessionConfirmation}`,
    );
  }

  protected async navigateEndSession() {
    return await this.navigateTo(
      `${AppRoutePaths.EndSession}/${EndSessionRoutePaths.ClosingSession}`,
    );
  }

  protected async navigateStart() {
    return await this.navigateTo(`${AppRoutePaths.StartWorkflow}`);
  }

  protected async navigateToPage(pageKey: PageKey | null) {
    if (!pageKey) {
      return this.navigateEndSession();
    }

    const routePath = this._navigationService.getRoutePath(pageKey);

    if (!routePath) {
      this.navigateNotFound();
    }
    return await this.navigateTo(`${routePath}`);
  }

  public async navigateTo(routePath: string) {
    return await this._router.navigate([`${routePath}`]);
  }

  private logNavigationError(action: string, error: unknown) {
    this._logService.error(`[BaseComponent]${action} failed at ${this._router.url}`, error);
  }
}
